import React, { useEffect, useState } from 'react';
import { Grid, makeStyles, TextField, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store';
import { organizationActions } from 'actions/organizationActions';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import 'translations/translations';

type StepProps = {
  stepTitleString: string;
  buttonText: string;
};

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

const AddUserToOrganizationStep: React.FC<StepProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const [inputs, setInputs] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [emailError, setEmailError] = useState(false);

  const organizationId = useSelector(
    (state: ApplicationState) => state.organization?.id,
  );

  const organizationError = useSelector(
    (state: ApplicationState) => state.organization?.error,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleEmailChange = (name: string, value: string, error: boolean) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    setEmailError(error);
  };

  useEffect(() => {
    handleButtonState(
      inputs.contactName,
      inputs.contactEmail,
      inputs.contactPhone,
      emailError,
    );
  }, [inputs, emailError]);

  const handleButtonState = (
    contactName: string,
    contactEmail: string,
    contactPhone: string,
    error?: boolean,
  ): void => {
    if (error) {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
      return;
    }

    if ((contactName !== '' || contactPhone !== '') && contactEmail === '') {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
      return;
    }

    dispatch({ type: 'DISABLE_BUTTON', disabled: false });
  };

  const callbackFn = () => {
    if (inputs.contactEmail !== '') {
      dispatch(
        organizationActions.addAdminToOrg(
          organizationId,
          inputs.contactEmail,
          inputs.contactName,
          inputs.contactPhone,
        ),
      );
    } else {
      dispatch({ type: 'SET_STEP', step: 4 });
    }
  };

  useEffect(() => {
    dispatch({ type: 'SET_CALLBACK', callbackFn });
  });

  return (
    <>
      <Grid item xs={6}>
        <TextField
          variant="outlined"
          margin="none"
          fullWidth
          id="contactName"
          label={t('organizations_page.create_org.add_user_step.contact_name')}
          name="contactName"
          autoFocus={true}
          onChange={handleChange}
        ></TextField>
      </Grid>
      <Grid item xs={6}>
        <ValidatedTextInput
          type="email"
          margin="none"
          required={false}
          label={t('organizations_page.create_org.add_user_step.contact_email')}
          handleChangeCallback={handleEmailChange}
          inputName="contactEmail"
          autoFocus={false}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          variant="outlined"
          margin="none"
          fullWidth
          id="contactPhone"
          label={t('organizations_page.create_org.add_user_step.contact_phone')}
          name="contactPhone"
          autoFocus={false}
          onChange={handleChange}
        ></TextField>
      </Grid>
      <Typography className={classes.error}>
        {!!organizationError && organizationError}
      </Typography>
    </>
  );
};

export { AddUserToOrganizationStep };
